import * as React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CodeIcon from "@mui/icons-material/Code";
import AddIcon from "@mui/icons-material/Add";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function OrdersList({
  orders,
  loading,
  error,
  onRefresh,
  onViewAllJson,
  onAddOrder,
  onViewOrderJson,
  formatDate,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [expandedId, setExpandedId] = React.useState(null);
  const [expandedData, setExpandedData] = React.useState(null);
  const [expandedLoading, setExpandedLoading] = React.useState(false);

  const fetchOrderDetails = React.useCallback(async (orderId) => {
    setExpandedLoading(true);
    setExpandedData(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(
        `${API_BASE_URL}/admin/squarespace/orders/${encodeURIComponent(orderId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await response.json();
      if (response.ok) {
        setExpandedData(data.data || data);
      } else {
        setExpandedData({ error: data.message || data.error || "Failed to load" });
      }
    } catch (err) {
      setExpandedData({ error: err.message || "Failed to load" });
    } finally {
      setExpandedLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleRowClick = (order) => {
    const id = order?.id || order?.orderId;
    if (!id) return;
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedData(null);
    } else {
      setExpandedId(id);
      fetchOrderDetails(id);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Orders
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CodeIcon />}
            onClick={onViewAllJson}
          >
            View JSON
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddOrder}
          >
            Add Order
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : orders.length === 0 ? (
        <Alert severity="info">No orders found for this customer.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Order Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => {
                const rowId = order.id || order.orderId;
                const isExpanded = expandedId === rowId;
                const rowData = isExpanded ? expandedData : null;
                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(order)}
                      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {rowId || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.orderNumber || order.order_number || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status || "N/A"}
                          size="small"
                          color={
                            order.status === "COMPLETE" || order.status === "completed" ? "success" :
                            order.status === "PENDING" || order.status === "pending" ? "warning" :
                            order.status === "CANCELLED" || order.status === "cancelled" ? "error" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(order.createdAt || order.created_at || order.createdOn)}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => onViewOrderJson(order)}
                          aria-label="View order JSON"
                        >
                          <CodeIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 2, backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50] }}>
                          {expandedLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : rowData?.error ? (
                            <Alert severity="error">{rowData.error}</Alert>
                          ) : rowData ? (
                            <Box
                              component="pre"
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                                overflow: "auto",
                                maxHeight: 300,
                                margin: 0,
                              }}
                            >
                              {JSON.stringify(rowData, null, 2)}
                            </Box>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
